import { AssetType } from '@vimond/services/content-discovery';
import { getProductIds } from './getProductIds';

const FREE = 'FREE';
const FREE_LOGIN = 'FREE_LOGIN';
const FREE_NOLOGIN = 'FREE_NOLOGIN';
const FREE_ACCESS_TYPES = [FREE, FREE_LOGIN, FREE_NOLOGIN];
const FREE_NOLOGIN_ACCESS_TYPES = [FREE_NOLOGIN, FREE];

const assetProductsIncludeAccessTypes = (
  asset: AssetType,
  accessTypes: string[],
): boolean => {
  return asset.products.some((product) => {
    const productAccessType =
      product?.accessType?.toString().toUpperCase() ?? '';
    return accessTypes.includes(productAccessType);
  });
};

export const hasAccessToAsset = (
  asset: AssetType | undefined,
  token?: string,
) => {
  if (!asset) return false;

  const products = getProductIds(token ?? '');
  const isLoggedIn = !!token;

  const accessType = asset.accessInfo?.accessType?.toUpperCase() ?? '';

  const hasFreeOrFreeNoLoginProduct = assetProductsIncludeAccessTypes(
    asset,
    FREE_NOLOGIN_ACCESS_TYPES,
  );

  if (
    !isLoggedIn &&
    accessType !== FREE_NOLOGIN &&
    !hasFreeOrFreeNoLoginProduct
  ) {
    return false;
  }

  // Past this point, we know that the user is logged in

  if (
    accessType === FREE ||
    accessType === FREE_LOGIN ||
    accessType === FREE_NOLOGIN
  ) {
    return true;
  }

  if (asset.labeledAsFree) {
    return true;
  }

  const hasFreeProduct = assetProductsIncludeAccessTypes(
    asset,
    FREE_ACCESS_TYPES,
  );

  if (hasFreeProduct) {
    return true;
  }

  if (Array.isArray(asset.products) && products.size > 0) {
    // Check for regular product-based subscriptions
    const hasSubscription = asset.products.some((prod) => {
      if (typeof prod !== 'object' || prod === null || !('id' in prod))
        return false;
      const productIdStr = String(prod.id);
      const hasProduct = products.has(productIdStr);
      return hasProduct;
    });

    if (hasSubscription) {
      return true;
    }

    // Check for TVOD asset-level access (user purchased this specific asset)
    const assetIdStr = String(asset.id);
    const hasTvodAccess = products.has(assetIdStr);

    if (hasTvodAccess) {
      return true;
    }
  }

  if (
    !asset.products ||
    !Array.isArray(asset.products) ||
    asset.products.length === 0
  ) {
    return true;
  }
  return false;
};

export const isAssetFreeNoLogin = (asset: AssetType | undefined): boolean => {
  if (!asset) return false;

  // If asset's accessInfo exists, it should have high preference than product level access type
  if (asset.accessInfo?.accessType?.toUpperCase() === FREE_NOLOGIN) {
    return true;
  }

  if (asset.accessInfo?.accessType?.toUpperCase() === FREE_LOGIN) {
    return false;
  }

  if (asset.labeledAsFree) {
    return true;
  }

  if (asset.products.length === 0) {
    return true;
  }

  const isFreeProduct = assetProductsIncludeAccessTypes(
    asset,
    FREE_NOLOGIN_ACCESS_TYPES,
  );

  if (isFreeProduct) {
    return true;
  }

  return false;
};
